import { CANCER_SUBTYPE_PARAM_ID, CANCER_TYPE_PARAM_ID } from '@/config/constants';
import { useGlobalState } from '@/layers/presentation/context/GlobalStateContext';
import { useThrowNotification } from '@/layers/presentation/hooks';
import {
  trackGAEvent,
  trackGoogleAdsConversion,
} from '@/layers/presentation/components/utils/GAEventTracking';
import { GOOGLE_ADS_CONVERSIONS } from '@/layers/presentation/components/utils/GoogleAnalytics';
import { createSavedSearch, fetchSavedSearches } from '@/layers/presentation/services/searches';
import logger from '@/layers/support/monitoring/logger';
import { NotificationType, ParamType } from '@/types/modelEnums';
import { PatientParam, PatientParamValue, PatientParamValues, SavedSearch } from '@/types/models';
import { ProgramFilters } from '@/types/programs';
import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce, useDebouncedCallback } from 'use-debounce';

const LOG_MODULE = '[Hooks / useProgramSearch]';

const IS_TEST = typeof process !== 'undefined' && process.env.NODE_ENV === 'test';
const DEBOUNCE_UI_TO_PARAMS_MS = IS_TEST ? 50 : 300;
const DEBOUNCE_FILTERS_MS = IS_TEST ? 50 : 1000;
const DEBOUNCE_URL_HASH_MS = IS_TEST ? 50 : 1000;

export type HandleParamChangeType = (props: HandleParamChangeProps) => void;
export type HandleQueryChangeType = (props: HandleQueryChangeProps) => void;

export type HandleParamChangeProps = {
  paramId: number;
  type: ParamType;
  paramValue: PatientParamValue;
};

export type HandleQueryChangeProps = {
  query: string;
};

export interface SyncCancerParamsFromQueryArgs {
  query: string;
  selectedOptions: PatientParamValues;
  patientParamsDictionary: PatientParam[];
  mode?: 'force' | 'fill-missing';
}

export function syncCancerParamsFromQuery({
  query,
  selectedOptions,
  patientParamsDictionary,
  mode = 'force',
}: SyncCancerParamsFromQueryArgs): {
  nextSelectedOptions: PatientParamValues;
  didChange: boolean;
} {
  const hasCancerType = Boolean(selectedOptions[CANCER_TYPE_PARAM_ID]);
  const hasCancerSubtype = Boolean(selectedOptions[CANCER_SUBTYPE_PARAM_ID]);

  if (mode === 'fill-missing' && (hasCancerType || hasCancerSubtype)) {
    return { nextSelectedOptions: selectedOptions, didChange: false };
  }

  // Clearing should always be possible (e.g., via the autocomplete clear button).
  // But when we're in "fill-missing" mode (URL hydration), we don't want to wipe
  // existing param selections just because the URL query is empty.
  if (query === '') {
    if (mode === 'fill-missing') {
      return { nextSelectedOptions: selectedOptions, didChange: false };
    }

    const nextSelectedOptions = { ...selectedOptions };
    delete nextSelectedOptions[CANCER_TYPE_PARAM_ID];
    delete nextSelectedOptions[CANCER_SUBTYPE_PARAM_ID];
    const didChange = JSON.stringify(nextSelectedOptions) !== JSON.stringify(selectedOptions);
    return { nextSelectedOptions, didChange };
  }

  const cancerTypeParam = patientParamsDictionary.find(param => param.id === CANCER_TYPE_PARAM_ID);
  const cancerSubtypeParam = patientParamsDictionary.find(
    param => param.id === CANCER_SUBTYPE_PARAM_ID,
  );
  const cancerTypes = cancerTypeParam?.options || [];
  const cancerSubtypes = cancerSubtypeParam?.options || [];

  // Don't attempt to sync while the dictionary is not ready.
  if (cancerTypes.length === 0) {
    return { nextSelectedOptions: selectedOptions, didChange: false };
  }

  let nextSelectedOptions = { ...selectedOptions };
  let foundCancerTypeMatch = false;
  let foundCancerSubtypeMatch = false;

  if (query !== '' && query.length > 3) {
    if (query.includes(',')) {
      const parts = query.split(',').map(part => part.trim());

      if (parts.length > 0) {
        const cancerTypeText = parts[0];
        const matchingType = cancerTypes.find(
          type => type.option.toLowerCase() === cancerTypeText.toLowerCase(),
        );

        if (matchingType) {
          nextSelectedOptions = {
            ...nextSelectedOptions,
            [CANCER_TYPE_PARAM_ID]: [matchingType.id],
          };
          foundCancerTypeMatch = true;

          if (parts.length > 1) {
            const subtypeText = parts[1];
            const filteredSubtypes = cancerSubtypes.filter(
              subtype => subtype.parent_option_id === matchingType.id,
            );
            const matchingSubtype = filteredSubtypes.find(
              subtype =>
                subtype.option && subtype.option.toLowerCase() === subtypeText.toLowerCase(),
            );

            if (matchingSubtype) {
              nextSelectedOptions = {
                ...nextSelectedOptions,
                [CANCER_SUBTYPE_PARAM_ID]: [matchingSubtype.id],
              };
              foundCancerSubtypeMatch = true;
            }
          }
        }
      }
    } else {
      const matchingType = cancerTypes.find(
        type => type.option.toLowerCase() === query.toLowerCase(),
      );

      if (matchingType) {
        nextSelectedOptions = {
          ...nextSelectedOptions,
          [CANCER_TYPE_PARAM_ID]: [matchingType.id],
        };
        foundCancerTypeMatch = true;
      }
    }

    if (!foundCancerTypeMatch && query !== '') {
      if (nextSelectedOptions[CANCER_TYPE_PARAM_ID]) {
        delete nextSelectedOptions[CANCER_TYPE_PARAM_ID];
      }
    }

    if (!foundCancerSubtypeMatch && query !== '') {
      if (nextSelectedOptions[CANCER_SUBTYPE_PARAM_ID]) {
        delete nextSelectedOptions[CANCER_SUBTYPE_PARAM_ID];
      }
    }
  }

  const didChange = JSON.stringify(nextSelectedOptions) !== JSON.stringify(selectedOptions);
  return { nextSelectedOptions, didChange };
}

export function useProgramSearch() {
  const {
    user,
    patientParamsDictionary,
    isLoadingParams,
    error,
    selectedOptions,
    setSelectedOptions,
    query,
    setQuery,
  } = useGlobalState();

  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [selectedSearch, setSelectedSearch] = useState<string>('');
  const [selectedTabKey, setSelectedTabKey] = useState<string>('search');
  const [isAdvancedSearchVisible, setAdvancedSearchVisible] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [initialSettleReady] = useState(true);
  const skipFirstUrlSyncRef = useRef(true);
  const throwNotification = useThrowNotification();
  const hasTrackedConversion = useRef(false);

  const toggleAdvancedSearch = useCallback(() => {
    setAdvancedSearchVisible(prev => !prev);
  }, []);

  const updatSearchWithFilters = useCallback(
    (selectedOptions: PatientParamValues, query: string | undefined) => {
      logger.debug(`${LOG_MODULE} updateHashWithFilters`, {
        selectedOptions,
        query,
      });

      const params = new URLSearchParams();
      Object.entries(selectedOptions).forEach(([key, paramValue]) => {
        if (Array.isArray(paramValue) && paramValue.length > 0) {
          params.set(`param_${key}`, paramValue.join(','));
        } else if (!Array.isArray(paramValue) && paramValue) {
          params.set(`param_${key}`, paramValue);
        }
      });
      if (query !== undefined) {
        params.set('query', query);
      }
      const newSearch = params.toString() ? `?${params.toString()}` : '';

      const newUrl = `${window.location.pathname}${newSearch}${window.location.hash}`;
      window.history.pushState({ path: newUrl }, '', newUrl);

      // Fire conversion event only once per search session when there's a query
      if (query && query.length > 0 && !hasTrackedConversion.current) {
        hasTrackedConversion.current = true;
        trackGAEvent('program_search', { lead_stage: 'micro' });
        trackGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.SUBMIT_LEAD_FORM);
      }
    },
    [],
  );

  const debouncedUpdate = useDebouncedCallback(
    (nextSelected: PatientParamValues, nextQuery: string | undefined) => {
      updatSearchWithFilters(nextSelected, nextQuery);
    },
    DEBOUNCE_URL_HASH_MS,
  );

  const [debouncedSelectedOptions] = useDebounce<PatientParamValues>(
    selectedOptions,
    DEBOUNCE_FILTERS_MS,
  );
  const [debouncedQuery] = useDebounce<string | undefined>(query, DEBOUNCE_FILTERS_MS);

  // For ProgramSearchFilters (strict, immediate UI)
  const uiFiltersStrict = useMemo(
    () => ({ patientParams: selectedOptions || {}, query }),
    [selectedOptions, query],
  );

  // For ProgramsList (debounced searching)
  const listFilters = useMemo<ProgramFilters>(
    // On the very first load, `query` starts as `undefined` and only becomes defined after
    // parsing URL params. `debouncedQuery` can lag behind by DEBOUNCE_FILTERS_MS, which
    // delays the initial fetch and keeps the "global skeleton" visible longer than needed.
    // We only fall back to the immediate `query` when the debounced value is still undefined.
    () => ({ patientParams: debouncedSelectedOptions, query: debouncedQuery ?? query }),
    [debouncedSelectedOptions, debouncedQuery, query],
  );

  const parseParamsFromSearch = useCallback((): {
    selectedOptions: PatientParamValues;
    query: string;
  } => {
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const selectedOptions: PatientParamValues = {};

    params.forEach((value, key) => {
      if (key.startsWith('param_')) {
        const paramId = key.replace('param_', '');

        // Handle numeric param values (like cancer type/subtype)
        if (
          paramId === String(CANCER_TYPE_PARAM_ID) ||
          paramId === String(CANCER_SUBTYPE_PARAM_ID)
        ) {
          selectedOptions[paramId] = value.split(',').map(Number);
        } else {
          try {
            // Try to parse as numbers if possible
            selectedOptions[paramId] = value.split(',').map(Number);
          } catch (e) {
            // If parsing fails, keep as string
            selectedOptions[paramId] = value;
          }
        }
      }
    });

    // We set query to empty only on the search pages
    // To fetch and show the default recent programs
    const query = params.get('query') ?? '';
    logger.debug(`${LOG_MODULE} parseParamsFromHash`, { selectedOptions, query });
    return { selectedOptions, query };
  }, []);

  // Memoize getAllOptionTexts since it depends on patientParamsDictionary
  const getAllOptionTexts = useCallback(
    (selectedOptions: PatientParamValues): string => {
      const optionTexts: string[] = [];

      const optionsToProcess = Object.entries(selectedOptions);
      optionsToProcess.forEach(([paramId, paramValue]) => {
        const param = patientParamsDictionary.find(p => p.id === Number(paramId));
        if (Array.isArray(paramValue) && param) {
          const texts = paramValue
            .map(optionId => param.options.find(o => o.id === optionId)?.option)
            .filter(Boolean) as string[];
          optionTexts.push(...texts);
        }
      });

      return optionTexts.join(', ');
    },
    [patientParamsDictionary],
  );

  const updateQueryFromParams = useCallback(
    (
      prevOptions: PatientParamValues,
      updatedOptions: PatientParamValues,
      paramId: number,
      paramValue: PatientParamValue,
    ) => {
      const noRealChange = JSON.stringify(prevOptions[paramId]) === JSON.stringify(paramValue);

      if (noRealChange) {
        return;
      }

      // When cancer type/subtype is changed
      if (paramId === CANCER_TYPE_PARAM_ID || paramId === CANCER_SUBTYPE_PARAM_ID) {
        const filteredOptions = Object.fromEntries(
          Object.entries(updatedOptions).filter(
            ([id]) => id === String(CANCER_TYPE_PARAM_ID) || id === String(CANCER_SUBTYPE_PARAM_ID),
          ),
        );

        const cancerText = getAllOptionTexts(filteredOptions);
        setQuery(cancerText);

        logger.debug(`${LOG_MODULE} handleParamChange: updated query with cancer info`, {
          cancerText,
          updatedQuery: cancerText,
        });
      }
    },
    [getAllOptionTexts, setQuery],
  );

  const updateParamFromQuery = useCallback(
    (query: string, selectedOptions: PatientParamValues) => {
      const { nextSelectedOptions, didChange } = syncCancerParamsFromQuery({
        query,
        selectedOptions,
        patientParamsDictionary,
      });
      if (!didChange) return;
      setSelectedOptions(nextSelectedOptions);
    },
    [patientParamsDictionary, setSelectedOptions],
  );

  // Debounce query -> params sync to keep typing responsive
  const debouncedUpdateParamFromQuery = useDebouncedCallback(
    (nextQuery: string, currentSelected: PatientParamValues) => {
      updateParamFromQuery(nextQuery, currentSelected);
    },
    DEBOUNCE_UI_TO_PARAMS_MS,
  );

  const handleParamChange: HandleParamChangeType = useCallback(
    (props: HandleParamChangeProps) => {
      setSelectedOptions(prevOptions => {
        const updatedOptions: PatientParamValues = {
          ...prevOptions,
          [props.paramId]: props.paramValue,
        };

        updateQueryFromParams(prevOptions, updatedOptions, props.paramId, props.paramValue);
        return updatedOptions;
      });
    },
    [setSelectedOptions, updateQueryFromParams],
  );

  const handleQueryChange: HandleQueryChangeType = useCallback(
    (props: HandleQueryChangeProps) => {
      setQuery(props.query);

      // Clearing should be immediate (and cancel any pending debounced updates),
      // otherwise the cancer param can get "stuck" or re-applied after a clear.
      if (props.query === '') {
        debouncedUpdateParamFromQuery.cancel();
        updateParamFromQuery('', selectedOptions);
      } else {
        // Debounced params update after immediate UI update
        debouncedUpdateParamFromQuery(props.query, selectedOptions);
      }

      // Keep the search tab selected
      setSelectedTabKey('search');
    },
    [
      setQuery,
      setSelectedTabKey,
      debouncedUpdateParamFromQuery,
      selectedOptions,
      updateParamFromQuery,
    ],
  );

  const handleSaveSearch = useCallback(
    async (params: PatientParamValues, query: string) => {
      logger.debug(`${LOG_MODULE} handleSaveSearch: begin`, { params, query });
      setIsSaving(true);
      try {
        const newSearch: Partial<SavedSearch> = {
          patient_params: params,
          query,
          user_id: user?.id,
        };
        const savedSearch = await createSavedSearch(newSearch);
        logger.debug(`${LOG_MODULE} handleSaveSearch: end`, {
          newSearch,
          savedSearch,
        });

        // Show success notification
        throwNotification(NotificationType.Success, 'Search saved successfully!');
      } catch (error) {
        logger.error(`${LOG_MODULE} handleSaveSearch`, { error });
        throwNotification(NotificationType.Error, 'Failed to save search');
        return;
      } finally {
        setIsSaving(false);
      }

      // Reload the saved-searches list as a best-effort follow-up. The save has
      // already succeeded, so a reload failure must NOT surface as a "Failed to
      // save search" error toast (that was the dual-toast bug on the patient page).
      try {
        const searches = await fetchSavedSearches(user!.id);
        setSavedSearches(
          searches.sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
          ),
        );
      } catch (reloadError) {
        logger.error(`${LOG_MODULE} handleSaveSearch: reload after save failed`, { reloadError });
      }
    },
    [user, throwNotification],
  );

  const handleSearchSelect = useCallback(
    (searchId: string) => {
      logger.debug(`${LOG_MODULE} handleSearchSelect`, { searchId });
      setSelectedSearch(searchId);
      const search = savedSearches.find(s => s.id.toString() === searchId);
      if (search && search.patient_params) {
        setSelectedOptions(search.patient_params);
        setQuery(search.query || '');
        updatSearchWithFilters(search.patient_params, search.query || '');
        logger.debug(`${LOG_MODULE} handleSearchSelect: setSelectedOptions and setQuery`, {
          search,
          selectedOptions,
          query,
        });
      }
    },
    [
      savedSearches,
      setSelectedSearch,
      setSelectedOptions,
      setQuery,
      selectedOptions,
      query,
      updatSearchWithFilters,
    ],
  );

  const summarizePatientParams = useCallback(
    (params: PatientParamValues, query: string | undefined) => {
      logger.debug(`${LOG_MODULE} summarizePatientParams: begin`, { params });
      let result = Object.entries(params)
        .map(([paramId, paramValue]) => {
          const param = patientParamsDictionary.find((p: any) => p.id === parseInt(paramId));
          if (!param) return '';
          if (Array.isArray(paramValue)) {
            const optionStrings = paramValue
              .map(o => param.options.find((po: any) => po.id === o)?.option)
              .filter(Boolean);
            return `${param.name}: ${optionStrings.join(', ')}`;
          } else {
            return `${param.name}: ${paramValue}`;
          }
        })
        .filter(Boolean)
        .join('; ');

      if (query) {
        result = `Query: "${query}"; ${result}`;
      }

      logger.debug(`${LOG_MODULE} summarizePatientParams: end`, { result });
      return result;
    },
    [patientParamsDictionary],
  );

  const formatSearchName = useCallback(
    (search: SavedSearch) => {
      const date = format(new Date(search.created_at), 'yyyy-MM-dd HH:mm');
      const paramsSummary = summarizePatientParams(search.patient_params, search.query);
      return `${date} - ${paramsSummary}`;
    },
    [summarizePatientParams],
  );

  useEffect(() => {
    const loadSavedSearches = async () => {
      logger.debug(`${LOG_MODULE} loadSavedSearches: begin`);
      try {
        const searches = await fetchSavedSearches(user!.id);
        logger.debug(`${LOG_MODULE} loadSavedSearches: end`, { searches });
        // Sort searches by created_at in descending order
        setSavedSearches(
          searches.sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
          ),
        );
      } catch (error) {
        logger.error(`${LOG_MODULE} loadSavedSearches`, { error });
      }
    };

    if (user?.id) {
      loadSavedSearches();
    }
  }, [user]);

  // Initial load parse & a one-shot settle window
  useEffect(() => {
    // Initial parse from hash
    const { selectedOptions, query: urlQuery } = parseParamsFromSearch();

    // If the user already changed the query (before params dictionary finished loading),
    // don't overwrite their input by re-parsing the URL on dictionary updates.
    if (query !== undefined && query !== urlQuery) {
      return;
    }

    const { nextSelectedOptions } = syncCancerParamsFromQuery({
      query: urlQuery,
      selectedOptions,
      patientParamsDictionary,
      mode: 'fill-missing',
    });
    setSelectedOptions(nextSelectedOptions);
    setQuery(urlQuery);

    // Mark initial load as complete after setting the state
    setIsInitialLoad(false);

    // Set up listener for browser back/forward navigation
    const handlePopState = () => {
      const { selectedOptions, query } = parseParamsFromSearch();
      const { nextSelectedOptions } = syncCancerParamsFromQuery({
        query,
        selectedOptions,
        patientParamsDictionary,
        mode: 'fill-missing',
      });
      setSelectedOptions(nextSelectedOptions);
      setQuery(query);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [setSelectedOptions, setQuery, parseParamsFromSearch, patientParamsDictionary, query]);

  useEffect(() => {
    if (!isInitialLoad && initialSettleReady) {
      if (skipFirstUrlSyncRef.current) {
        skipFirstUrlSyncRef.current = false;
        return;
      }
      debouncedUpdate(selectedOptions, query);
    }
  }, [
    selectedOptions,
    query,
    updatSearchWithFilters,
    isInitialLoad,
    debouncedUpdate,
    initialSettleReady,
  ]);

  return {
    selectedOptions,
    debouncedSelectedOptions,
    savedSearches,
    selectedSearch,
    selectedTabKey,
    patientParamsDictionary,
    isLoadingParams,
    error,
    handleParamChange,
    handleSaveSearch,
    handleSearchSelect,
    formatSearchName,
    setSelectedTabKey,
    query,
    debouncedQuery,
    setQuery,
    handleQueryChange,
    isAdvancedSearchVisible,
    toggleAdvancedSearch,
    isSaving,
    isInitialLoad,
    initialSettleReady,
    uiFiltersStrict,
    listFilters,
  };
}

export type UseProgramSearchReturn = ReturnType<typeof useProgramSearch>;
